import {
	Fn,
	vec3,
	float,
	dot,
	normalize,
	reflect,
	max,
	If,
} from 'three/tsl';

import { struct } from './patches.js';

import { AnisoFrame, DotProducts } from './Struct.js';
import { PI, MIN_CLEARCOAT_ROUGHNESS, computeDotProductsAniso, anisoTangentFrame } from './Common.js';
import { DistributionGGX, computeAnisoAlphas, calculateVNDFPDFAniso } from './MaterialProperties.js';
import { ImportanceSampleGGX, ImportanceSampleCosine, sampleGGXVNDFAniso } from './MaterialSampling.js';
import { evaluateLayeredBRDF } from './MaterialEvaluation.js';
import { RandomValue } from './Random.js';

export const ClearcoatResult = struct( {
	brdf: 'vec3',
	L: 'vec3',
	pdf: 'float',
} );

// Improved clearcoat sampling function
// Returns vec4: xyz = brdf color, w = pdf
// L (light direction) is returned via the out pattern as a separate return
export const sampleClearcoat = Fn( ( [
	ray, hitInfo, material, randomSample, rngState,
] ) => {

	const N = hitInfo.normal;
	const V = ray.direction.negate();

	// Clamp clearcoat roughness to avoid artifacts
	const clearcoatRoughness = max( material.clearcoatRoughness, MIN_CLEARCOAT_ROUGHNESS );
	const baseRoughness = max( material.roughness, MIN_CLEARCOAT_ROUGHNESS );

	// Calculate sampling weights based on material properties
	const specularWeight = float( 1.0 ).sub( baseRoughness ).mul( float( 0.5 ).add( float( 0.5 ).mul( material.metalness ) ) ).toVar();
	const clearcoatWeight = material.clearcoat.mul( float( 1.0 ).sub( clearcoatRoughness ) ).toVar();
	const diffuseWeight = float( 1.0 ).sub( specularWeight ).mul( float( 1.0 ).sub( material.metalness ) ).toVar();

	// Normalize weights
	const total = specularWeight.add( clearcoatWeight ).add( diffuseWeight );
	specularWeight.divAssign( total );
	clearcoatWeight.divAssign( total );
	diffuseWeight.divAssign( total );

	// Choose which layer to sample
	const rand = RandomValue( rngState );

	const L = vec3( 0.0 ).toVar();
	const H = vec3( 0.0 ).toVar();

	If( rand.lessThan( clearcoatWeight ), () => {

		// Sample clearcoat layer
		H.assign( ImportanceSampleGGX( { N, roughness: clearcoatRoughness, Xi: randomSample } ) );
		L.assign( reflect( V.negate(), H ) );

	} ).ElseIf( rand.lessThan( clearcoatWeight.add( specularWeight ) ), () => {

		// Sample base specular (anisotropic VNDF when anisotropy > 0)
		If( material.anisotropy.greaterThan( 0.0 ), () => {

			const f = AnisoFrame.wrap( anisoTangentFrame( N, material.anisotropyRotation ) );
			const localV = vec3( dot( V, f.Ta ), dot( V, f.Ba ), dot( V, N ) );
			const a = computeAnisoAlphas( material.roughness, material.anisotropy );
			const localH = sampleGGXVNDFAniso( { V: localV, alphaX: a.x, alphaY: a.y, Xi: randomSample } );
			H.assign( f.Ta.mul( localH.x ).add( f.Ba.mul( localH.y ) ).add( N.mul( localH.z ) ) );

		} ).Else( () => {

			H.assign( ImportanceSampleGGX( { N, roughness: baseRoughness, Xi: randomSample } ) );

		} );
		L.assign( reflect( V.negate(), H ) );

	} ).Else( () => {

		// Sample diffuse
		L.assign( ImportanceSampleCosine( { N, xi: randomSample } ) );
		H.assign( normalize( V.add( L ) ) );

	} );

	// Calculate dot products (aniso-aware: also projects onto the anisotropy frame)
	const dots = DotProducts.wrap( computeDotProductsAniso( N, V, L, material ) );

	// Calculate individual PDFs. Clearcoat layer is always isotropic; the base specular
	// matches its sampler — VNDF-aniso when anisotropy>0, else the half-vector GGX pdf.
	const clearcoatPDF = DistributionGGX( dots.NoH, clearcoatRoughness ).mul( dots.NoH ).div( float( 4.0 ).mul( dots.VoH ) ).mul( clearcoatWeight );
	const specularPDF = float( 0.0 ).toVar();
	If( material.anisotropy.greaterThan( 0.0 ), () => {

		const a = computeAnisoAlphas( material.roughness, material.anisotropy );
		specularPDF.assign( calculateVNDFPDFAniso( a.x, a.y, dots.NoH, dots.ToH, dots.BoH, dots.NoV, dots.ToV, dots.BoV ).mul( specularWeight ) );

	} ).Else( () => {

		specularPDF.assign( DistributionGGX( dots.NoH, baseRoughness ).mul( dots.NoH ).div( float( 4.0 ).mul( dots.VoH ) ).mul( specularWeight ) );

	} );
	const diffusePDF = dots.NoL.div( PI ).mul( diffuseWeight );

	// Combined PDF using MIS
	const pdf = max( clearcoatPDF.add( specularPDF ).add( diffusePDF ), 0.001 );

	// Evaluate complete BRDF
	const brdf = evaluateLayeredBRDF( dots, material );

	// Return brdf, L direction, and pdf packed together
	// Caller needs L and pdf - return as struct-like output
	// We pack: result.xyz = brdf, result.w = pdf, L stored in separate output
	return ClearcoatResult( { brdf, L, pdf } );

} );
